import { SecondaryButton } from "@common/Buttons";
import SearchArea from "./components/SearchArea";
import TableArea from "./components/TableArea";
import { useEffect, useState } from "react";
import {
  useLazyFetchAdvanceSearchResultsQuery,
  useSaveAdvanceSearchMutation,
} from "@store/advanceSearch";
import { DatePicker } from "antd";
import { useDownloadFileMutation } from "@store/file";
import { downloadBlobAsFile } from "@store/utilities";
import dayjs from "dayjs";
import { getCurrentMonth, getPrevThirdMonth } from "@utils/helpers";

export default function AdvanceSearch() {
  const [selectedFilters, setSelectedFilters] = useState([]);
  const [selectedPrimaryProducts, setSelectedPrimaryProducts] = useState([]);
  const [secondaryFilters, setSecondaryFilters] = useState({});
  const [dateRange, setDateRange] = useState([null, null]);
  const [
    secondaryFiltersForExcelDownload,
    setSecondaryFiltersForExcelDownload,
  ] = useState({});
  const [hiddenFilters, setHiddenFilters] = useState([]);
  const [filters, setFilters] = useState([]);
  const [tableData, setTableData] = useState(null);
  const [sortConfig, setSortConfig] = useState({
    sortKey: "",
    sortOrder: "asc",
  });
  const [searchConfig, setSearchConfig] = useState(null);
  const [isSaveSearchFormOpen, setIsSaveSearchFormOpen] = useState(false);
  const [selectedSavedSearch, setSelectedSavedSearch] = useState(null);
  const [searchTrigger, setSearchTrigger] = useState(0);

  const [fetchAdvanceSearchResult, { isLoading, isFetching }] =
    useLazyFetchAdvanceSearchResultsQuery();
  const [saveAdvanceSearchFilters, { isLoading: isAdvanceFilterSaving }] =
    useSaveAdvanceSearchMutation();

  const [downloadFile, { isLoading: downloadFileLoading }] =
    useDownloadFileMutation();

  //handle save search
  const handleSaveSearch = (name = "") => {
    const payload = {
      name,
      payload: searchConfig,
    };

    saveAdvanceSearchFilters(payload).then((res) => {
      if (res?.data.success) {
        setIsSaveSearchFormOpen(false);
      }
    });
  };

  //handle search
  const handleSearch = (pageArg, sortKey = null, sortOrder = null, perPage = 10) => {
    const currentPage = typeof pageArg === "number" ? pageArg : 1;

    const payload = {
      ids: selectedPrimaryProducts,
      primaryFieldName: selectedFilters.at(-1)?.name,
      page: currentPage,
      perPage,
      fromDate: dayjs(dateRange[0]).format("MMMM-YYYY"),
      toDate: dayjs(dateRange[1]).format("MMMM-YYYY"),
    };

    if (selectedFilters[0]?.name === "Manufacturer") {
      payload.manufacturerIds = selectedPrimaryProducts;
      payload.consigneeIds = secondaryFilters.Consignee || [];
      payload.countryConsigneeIds =
        secondaryFilters["Country (Consignee)"] || [];
      payload.countryManufacturerIds =
        secondaryFilters["Country (Manufacturer)"] || [];
      payload.productIds = secondaryFilters.Product || [];
    }
    if (selectedFilters[0]?.name === "Consignee") {
      payload.consigneeIds = selectedPrimaryProducts;
      payload.manufacturerIds = secondaryFilters.Manufacturer || [];
      payload.countryConsigneeIds =
        secondaryFilters["Country (Consignee)"] || [];
      payload.countryManufacturerIds =
        secondaryFilters["Country (Manufacturer)"] || [];
      payload.productIds = secondaryFilters.Product || [];
    }
    if (selectedFilters[0]?.name === "Country (Consignee)") {
      payload.countryConsigneeIds = selectedPrimaryProducts;
      payload.manufacturerIds = secondaryFilters.Manufacturer || [];
      payload.consigneeIds = secondaryFilters.Consignee || [];
      payload.countryManufacturerIds =
        secondaryFilters["Country (Manufacturer)"] || [];
      payload.productIds = secondaryFilters.Product || [];
    }
    if (selectedFilters[0]?.name === "Country (Manufacturer)") {
      payload.countryManufacturerIds = selectedPrimaryProducts;
      payload.manufacturerIds = secondaryFilters.Manufacturer || [];
      payload.consigneeIds = secondaryFilters.Consignee || [];
      payload.countryConsigneeIds =
        secondaryFilters["Country (Consignee)"] || [];
      payload.productIds = secondaryFilters.Product || [];
    }
    if (selectedFilters[0]?.name === "Product") {
      payload.productIds = selectedPrimaryProducts;
      payload.countryManufacturerIds =
        secondaryFilters["Country (Manufacturer)"] || [];
      payload.manufacturerIds = secondaryFilters.Manufacturer || [];
      payload.consigneeIds = secondaryFilters.Consignee || [];
      payload.countryConsigneeIds =
        secondaryFilters["Country (Consignee)"] || [];
    }

    if (sortKey && sortOrder) {
      payload.sortKey = sortKey;
      payload.sortOrder = sortOrder;
      setSortConfig({ sortKey, sortOrder });
    } else {
      setSortConfig({ sortKey: "", sortOrder: "asc" });
    }

    const { month, year } = getPrevThirdMonth();

    setSearchConfig({
      sortKey,
      sortOrder,
      selectedPrimaryProducts,
      selectedFilters,
      dateRange: [dateRange[0], dateRange[1]],
      secondaryFilters,
      hiddenFilters,
      filters,
    });

    fetchAdvanceSearchResult(payload).then((res) => {
      if (res?.data?.success) {
        setTableData(res.data);
        const filters = secondaryFilters;
        setSecondaryFiltersForExcelDownload(filters);
        // setSecondaryFilters({});
      }
    });
  };

  //exporting excel
  const handleExportExcel = async () => {
    const payload = {};

    if (selectedFilters.length) {
      if (selectedFilters[0].name === "Manufacturer") {
        payload.manufacturerIds = selectedPrimaryProducts;
        payload.consigneeIds = secondaryFiltersForExcelDownload.Consignee || [];
        payload.countryConsigneeIds =
          secondaryFiltersForExcelDownload["Country (Consignee)"] || [];
        payload.countryManufacturerIds =
          secondaryFiltersForExcelDownload["Country (Manufacturer)"] || [];
        payload.productIds = secondaryFiltersForExcelDownload.Product || [];
      }
      if (selectedFilters[0].name === "Consignee") {
        payload.consigneeIds = selectedPrimaryProducts;
        payload.manufacturerIds =
          secondaryFiltersForExcelDownload.Manufacturer || [];
        payload.countryConsigneeIds =
          secondaryFiltersForExcelDownload["Country (Consignee)"] || [];
        payload.countryManufacturerIds =
          secondaryFiltersForExcelDownload["Country (Manufacturer)"] || [];
        payload.productIds = secondaryFiltersForExcelDownload.Product || [];
      }
      if (selectedFilters[0].name === "Country (Consignee)") {
        payload.countryConsigneeIds = selectedPrimaryProducts;
        payload.manufacturerIds =
          secondaryFiltersForExcelDownload.Manufacturer || [];
        payload.consigneeIds = secondaryFiltersForExcelDownload.Consignee || [];
        payload.countryManufacturerIds =
          secondaryFiltersForExcelDownload["Country (Manufacturer)"] || [];
        payload.productIds = secondaryFiltersForExcelDownload.Product || [];
      }
      if (selectedFilters[0].name === "Country (Manufacturer)") {
        payload.countryManufacturerIds = selectedPrimaryProducts;
        payload.manufacturerIds =
          secondaryFiltersForExcelDownload.Manufacturer || [];
        payload.consigneeIds = secondaryFiltersForExcelDownload.Consignee || [];
        payload.countryConsigneeIds =
          secondaryFiltersForExcelDownload["Country (Consignee)"] || [];
        payload.productIds = secondaryFiltersForExcelDownload.Product || [];
      }
      if (selectedFilters[0].name === "Product") {
        payload.productIds = selectedPrimaryProducts;
        payload.countryManufacturerIds =
          secondaryFiltersForExcelDownload["Country (Manufacturer)"] || [];
        payload.manufacturerIds =
          secondaryFiltersForExcelDownload.Manufacturer || [];
        payload.consigneeIds = secondaryFiltersForExcelDownload.Consignee || [];
        payload.countryConsigneeIds =
          secondaryFiltersForExcelDownload["Country (Consignee)"] || [];
      }
    }

    payload.fromDate = dayjs(dateRange[0]).format("MMMM-YYYY");
    payload.toDate = dayjs(dateRange[1]).format("MMMM-YYYY");
    payload.sortKey = sortConfig.sortKey;
    payload.sortOrder = sortConfig.sortOrder;

    const response = await downloadFile({
      url: "advance-search-export",
      params: {
        ...payload,
      },
    });
    if (response) {
      downloadBlobAsFile(response.data, `export.xlsx`);
    }
  };

  const onChange = (dates, dateStrings) => {
    setDateRange(dates);
  };

  // useEffect(() => {
  //   const { month, year } = getPrevThirdMonth();
  //   setDateRange([dayjs(`${year} ${month}`, "YYYY MMMM"), dayjs()]);
  // }, []);

  useEffect(() => {
    if (selectedSavedSearch) {
      const { month, year } = getCurrentMonth();
      setHiddenFilters(selectedSavedSearch.hiddenFilters);
      setDateRange([
        dayjs(selectedSavedSearch.dateRange[0]),
        dayjs(`${year} ${month}`, "YYYY MMMM"),
      ]);
      setSecondaryFilters(selectedSavedSearch.secondaryFilters);
      setSelectedFilters(selectedSavedSearch.selectedFilters);
      setSelectedPrimaryProducts(selectedSavedSearch.selectedPrimaryProducts);
      setFilters(selectedSavedSearch.filters);
      setSearchTrigger((prev) => prev + 1);
    }
  }, [selectedSavedSearch]);

  useEffect(() => {
    if (!searchTrigger) return;

    handleSearch(1, selectedSavedSearch.sortKey, selectedSavedSearch.sortOrder);
  }, [searchTrigger]);

  return (
    <div className="h-full p-2 overflow-hidden rounded-lg md:p-5 animate-fadeIn bg-secondary-main">
      <div className="flex flex-col gap-5">
        <div className="flex flex-col items-start justify-between border-b md:items-center md:flex-row border-b-border">
          {/* label */}
          <label className="py-2 text-lg font-semibold leading-6 text-text-header">
            MI Analytics
          </label>
          <div className="flex items-center gap-2">
            <DatePicker.RangePicker
              picker="month"
              value={dateRange}
              onChange={onChange}
              format="MMM YYYY"
              className="!w-52"
            />

            <SecondaryButton
              onClick={handleExportExcel}
              loading={downloadFileLoading}
            >
              Export in XLSX
            </SecondaryButton>
          </div>
        </div>
      </div>

      <div className="mt-2 h-full flex md:flex-row flex-col gap-2.5 animate-fadeIn">
        {/* left side */}
        <SearchArea
          setSelectedFilters={setSelectedFilters}
          selectedFilters={selectedFilters}
          filters={filters}
          setFilters={setFilters}
          setSelectedPrimaryProducts={setSelectedPrimaryProducts}
          selectedPrimaryProducts={selectedPrimaryProducts}
          handleSearch={handleSearch}
          loadingData={isLoading || isFetching}
          setSecondaryFilters={setSecondaryFilters}
          hiddenFilters={hiddenFilters}
          setHiddenFilters={setHiddenFilters}
          onHandleSaveSearch={handleSaveSearch}
          isAdvanceFilterSaving={isAdvanceFilterSaving}
          isSaveSearchFormOpen={isSaveSearchFormOpen}
          setIsSaveSearchFormOpen={setIsSaveSearchFormOpen}
          setSelectedSavedSearch={setSelectedSavedSearch}
        />

        {/* right side */}
        <TableArea
          data={tableData}
          filterColumns={[...selectedFilters, ...filters]}
          setSecondaryFilters={setSecondaryFilters}
          secondaryFilters={secondaryFilters}
          handleSearch={handleSearch}
          hiddenFilters={hiddenFilters}
          loading={isLoading || isFetching}
        />
      </div>
    </div>
  );
}
